import { z } from 'zod';
import { tagNameSchema } from '../tag/tag';

/** The languages a task may be written or translated in (QZ-D12). */
export const LANGUAGES = ['sv', 'en', 'da', 'nb', 'fi', 'fr', 'es', 'it', 'de'] as const;

/** A task's difficulty (QZ-D14). */
export const DIFFICULTIES = ['easy', 'medium', 'hard'] as const;

/** The task types GPS Quiz persists; the triple "er" of `ItemOrdererer` is its spelling, not a typo (QZ-D16). */
export const TASK_TYPES = [
  'MultiChoiceQuestion',
  'FreetextQuestion',
  'TiebreakerQuestion',
  'NumericTiebreakerQuestion',
  'ItemOrdererer',
] as const;

/** A task's review status (QZ-D37). */
export const TASK_STATUSES = ['draft', 'approved', 'rejected'] as const;

/** A task's language, validated against `LANGUAGES` (QZ-D12). */
export const languageSchema = z.enum(LANGUAGES);
export type Language = z.infer<typeof languageSchema>;

/** A task's difficulty, validated against `DIFFICULTIES` (QZ-D14). */
export const difficultySchema = z.enum(DIFFICULTIES);
export type Difficulty = z.infer<typeof difficultySchema>;

/** A task's type, validated against `TASK_TYPES` (QZ-D16). */
export const taskTypeSchema = z.enum(TASK_TYPES);
export type TaskType = z.infer<typeof taskTypeSchema>;

/** A task's review status, validated against `TASK_STATUSES` (QZ-D37). */
export const taskStatusSchema = z.enum(TASK_STATUSES);
export type TaskStatus = z.infer<typeof taskStatusSchema>;

/** The kinds of document a task's source reference may cite (QZ-D93). */
export const SOURCE_KINDS = ['pdf', 'photos'] as const;

/** A source reference's document kind, validated against `SOURCE_KINDS` (QZ-D93). */
export const sourceKindSchema = z.enum(SOURCE_KINDS);
export type SourceKind = z.infer<typeof sourceKindSchema>;

/**
 * The kind of a draft's source reference, from its job's method (QZ-D93). The parameter is the
 * literal union, not `JobMethod`: `job.ts` imports `task.ts`, so the reverse import would be a
 * cycle. Callers narrow with `isMaterialMethod` first.
 */
export const sourceKindOf = (method: 'pdf' | 'photos'): SourceKind => method;

/** A non-empty string after trimming; the stored value is trimmed (QZ-D42). */
export const textSchema = z.string().trim().min(1);

/** A task's id (QZ-D42). */
export const taskIdSchema = z.uuid();

/** A stored timestamp (QZ-D42). */
export const timestampSchema = z.iso.datetime();

/** The task types a task is tagged with; at least one (QZ-D16). */
export const taskTypesSchema = z.array(taskTypeSchema).min(1);

/** A task's tags; one to three (QZ-D15). */
export const tagsSchema = z.array(tagNameSchema).min(1).max(3);

/** The languages a task may be shown in, if restricted beyond its translations (QZ-D12). */
export const languageLimitSchema = z.array(languageSchema).min(1);

/** The correct answer to a numeric tiebreaker question (QZ-D42). */
export const numericAnswerSchema = z.number();

/** The explanation shown after a task is answered (QZ-D39). */
export const explanationSchema = textSchema;

/** The reason a task was rejected (QZ-D37). */
export const rejectionReasonSchema = textSchema.max(500);

/** One language's content for a task (QZ-D42). */
export const taskTranslationSchema = z
  .object({
    text: textSchema,
    choices: z.array(textSchema).min(2).max(6).optional(),
    freetextAnswer: textSchema.max(60).optional(),
    items: z.array(textSchema).min(2).max(6).optional(),
  })
  .strip();
export type TaskTranslation = z.infer<typeof taskTranslationSchema>;

/** Where a task's content came from (QZ-D42). `kind` absent means `pdf` (QZ-D42, QZ-D93). */
export const sourceReferenceSchema = z
  .object({
    url: z.url({ protocol: /^https?$/ }).optional(),
    document: textSchema.optional(),
    page: z.number().int().positive().optional(),
    kind: sourceKindSchema.optional(),
  })
  .strip()
  .superRefine((value, ctx) => {
    if (!value.url && !value.document) {
      ctx.addIssue({ code: 'custom', path: ['url'], message: 'Give a URL or a document.' });
    }
    if (value.url && value.document) {
      ctx.addIssue({
        code: 'custom',
        path: ['document'],
        message: 'Give a URL or a document, not both.',
      });
    }
    if (value.page !== undefined && !value.document) {
      ctx.addIssue({ code: 'custom', path: ['page'], message: 'A page number needs a document.' });
    }
    if (value.kind !== undefined && !value.document) {
      ctx.addIssue({ code: 'custom', path: ['kind'], message: 'A kind needs a document.' });
    }
  });
export type SourceReference = z.infer<typeof sourceReferenceSchema>;

/** What generation's check found (QZ-D60). */
export const taskCheckSchema = z
  .object({
    outcome: z.enum(['passed', 'revised']),
    note: textSchema.max(500).optional(),
    similarTaskIds: z.array(taskIdSchema).min(1).max(3).optional(),
  })
  .strip();
export type TaskCheck = z.infer<typeof taskCheckSchema>;

/** A task in the task bank (QZ-D42). Every stored task, drafts included, satisfies this schema. */
export const taskSchema = z
  .object({
    taskId: taskIdSchema,
    status: taskStatusSchema,
    taskTypes: taskTypesSchema,
    difficulty: difficultySchema,
    tags: tagsSchema,
    originalLanguage: languageSchema,
    languageLimit: languageLimitSchema.optional(),
    numericAnswer: numericAnswerSchema.optional(),
    /** Absent on a task from an open-web job, which cites no source (QZ-D79). */
    sourceReference: sourceReferenceSchema.optional(),
    explanation: explanationSchema,
    translations: z.partialRecord(languageSchema, taskTranslationSchema),
    rejectionReason: rejectionReasonSchema.optional(),
    jobId: z.string().min(1).optional(),
    check: taskCheckSchema.optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
  })
  .strip()
  .superRefine((task, ctx) => {
    const hasType = (type: TaskType): boolean => task.taskTypes.includes(type);

    // T1: no duplicate taskTypes.
    if (new Set(task.taskTypes).size !== task.taskTypes.length) {
      ctx.addIssue({
        code: 'custom',
        path: ['taskTypes'],
        message: 'A task type is listed more than once.',
      });
    }

    // T2: no duplicate tags.
    if (new Set(task.tags).size !== task.tags.length) {
      ctx.addIssue({ code: 'custom', path: ['tags'], message: 'A tag is listed more than once.' });
    }

    // T3: the original language has a translation.
    if (!task.translations[task.originalLanguage]) {
      ctx.addIssue({
        code: 'custom',
        path: ['translations', task.originalLanguage],
        message: 'The original language has no translation.',
      });
    }

    for (const [language, translation] of Object.entries(task.translations) as [
      Language,
      TaskTranslation | undefined,
    ][]) {
      if (!translation) {
        continue;
      }

      // T4: choices present exactly when MultiChoiceQuestion is listed.
      if ((translation.choices !== undefined) !== hasType('MultiChoiceQuestion')) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'choices'],
          message: 'Choices are needed exactly when the multiple-choice type is listed.',
        });
      }

      // T5: freetextAnswer present exactly when FreetextQuestion is listed.
      if ((translation.freetextAnswer !== undefined) !== hasType('FreetextQuestion')) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'freetextAnswer'],
          message: 'A free-text answer is needed exactly when the free-text type is listed.',
        });
      }

      // T6: items present exactly when ItemOrdererer is listed.
      if ((translation.items !== undefined) !== hasType('ItemOrdererer')) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'items'],
          message: 'Items are needed exactly when the item-ordering type is listed.',
        });
      }

      // T7: no duplicate choices or items within a translation.
      if (translation.choices && new Set(translation.choices).size !== translation.choices.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'choices'],
          message: 'A choice is listed more than once.',
        });
      }
      if (translation.items && new Set(translation.items).size !== translation.items.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['translations', language, 'items'],
          message: 'An item is listed more than once.',
        });
      }
    }

    // T8: numericAnswer present exactly when NumericTiebreakerQuestion is listed.
    if ((task.numericAnswer !== undefined) !== hasType('NumericTiebreakerQuestion')) {
      ctx.addIssue({
        code: 'custom',
        path: ['numericAnswer'],
        message: 'A numeric answer is needed exactly when the numeric tiebreaker type is listed.',
      });
    }

    // T9: languageLimit, if present, has no duplicates and covers originalLanguage and every translation.
    if (task.languageLimit) {
      if (new Set(task.languageLimit).size !== task.languageLimit.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['languageLimit'],
          message: 'A language is listed more than once.',
        });
      }
      if (!task.languageLimit.includes(task.originalLanguage)) {
        ctx.addIssue({
          code: 'custom',
          path: ['languageLimit'],
          message: 'The language limit must include the original language.',
        });
      }
      for (const language of Object.keys(task.translations) as Language[]) {
        if (!task.languageLimit.includes(language)) {
          ctx.addIssue({
            code: 'custom',
            path: ['languageLimit'],
            message: 'The language limit must include every translated language.',
          });
        }
      }
    }

    // T10: rejectionReason present only when status is rejected.
    if (task.rejectionReason !== undefined && task.status !== 'rejected') {
      ctx.addIssue({
        code: 'custom',
        path: ['rejectionReason'],
        message: 'A rejection reason is only allowed when the task is rejected.',
      });
    }
  });
export type Task = z.infer<typeof taskSchema>;

/** QZ-D37: draft → approved | rejected; approved | rejected → draft. */
export const canTransition = (from: TaskStatus, to: TaskStatus): boolean =>
  from === 'draft' ? to !== 'draft' : to === 'draft';
